/**
 * App context provider for app-scoped routes (/app/:appId/*)
 * Fetches and provides the current app data based on the appId URL param
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { fetchApp, type AppRecord } from '@/services/app-service'
import type { HttpError } from '@/types'

interface AppContextValue {
  app: AppRecord | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

const AppContext = createContext<AppContextValue>({
  app: null,
  isLoading: true,
  error: null,
  refetch: () => {},
})

/**
 * api-client already schedules the sign-in redirect for 401s — treat auth
 * errors as "silence and wait for navigation" so we don't flash an error UI.
 */
function isAuthHttpError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const httpError = error as Partial<HttpError>
  return httpError.type === 'authentication' || httpError.status === 401
}

/**
 * Shown when the app couldn't be loaded — either the ID is bad or the backend
 * is down. Giving the user a retry is cheaper than forcing a full page reload.
 */
function AppLoadErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
        <div className="size-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Couldn't load this app</h2>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
          <a
            href="/app"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-6 py-2.5 text-sm font-medium text-foreground shadow-sm hover:bg-muted transition-colors"
          >
            Back to apps
          </a>
        </div>
      </div>
    </div>
  )
}

/**
 * Provides current app data to all children within /app/:appId/* routes
 */
export function AppProvider({ children }: { children: ReactNode }) {
  const { appId } = useParams<{ appId: string }>()
  const [app, setApp] = useState<AppRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadApp = async () => {
    if (!appId) {
      setApp(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await fetchApp(appId)
      setApp(data)
    } catch (err) {
      // Auth errors already trigger a redirect elsewhere — don't render a
      // dead-end error screen on top of a page that's about to navigate.
      if (isAuthHttpError(err)) {
        setApp(null)
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to load app')
      setApp(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadApp()
  }, [appId])

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <p className="text-sm text-muted-foreground">Loading app...</p>
        </div>
      </div>
    )
  }

  // Previously this component rendered `children` with an error in context and
  // most pages treated `app` as truthy — so the user saw blank/broken UI.
  // Now we render a visible fallback with a retry.
  if (error) {
    return <AppLoadErrorState message={error} onRetry={loadApp} />
  }

  return (
    <AppContext.Provider value={{ app, isLoading, error, refetch: loadApp }}>
      {children}
    </AppContext.Provider>
  )
}

/**
 * Hook to access the current app context
 */
export function useApp() {
  return useContext(AppContext)
}
